import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Select, MenuItem, Card, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, FormControl, InputLabel, Stack, Checkbox,
  FormControlLabel, Tooltip, Link, TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  DeleteOutline as DeleteOutlineIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useSnackbar } from 'notistack';

const PRIMARY = '#4c5e8a';

type TierLabel = 'DEAD' | 'POTENTIAL' | 'SLOW' | 'HEALTHY' | 'NEW';
type TierFilter = 'all' | TierLabel;
// Season filter: '' = all; 春/夏/秋/冬/四季 = exact membership; 'none' = untagged
type SeasonFilter = '' | '春' | '夏' | '秋' | '冬' | '四季' | 'none';

interface StorefrontSide {
  product_id: number;
  shopify_id: number | null;
  handle: string;
  title: string;
  product_type: string;
  status: string;
  price_min: number;
  price_max: number;
  stock_total: number;
  image_url: string | null;
  listed_at: string | null;
  sales_count: number;
  daily_velocity: number;
  sku_count: number;
  views_180d: number;
  atc_180d: number;
  units_180d: number;
  revenue_180d: number;
  last_sale_date: string | null;
}

interface InventoryItem {
  sku: string | null;
  title: string;
  image_url: string;
  product_type: string;
  tier: TierLabel;
  tier_priority: number;
  reasons: string[];
  listed_at: string | null;
  days_since_listing: number | null;
  last_sale_date: string | null;
  days_since_sale: number | null;
  units_180d: number;
  views_180d: number;
  atc_180d: number;
  cvr_180d: number | null;
  atc_rate_180d: number | null;
  atc_to_buy_180d: number | null;
  revenue_180d: number;
  seasons: string[] | null;
  storefront: StorefrontSide;
}

interface InventoryResponse {
  total_sku_count: number;
  eligible_count?: number;  // pre-tier-filter total (new field, optional for backward compat)
  filtered: number;
  returned: number;
  offset: number;
  limit: number;
  tier_counts: Record<TierLabel, number>;
  storefront_base_url: string;
  metrics_window_days: number;
  products: InventoryItem[];
}

const TIER_META: Record<TierLabel, { label: string; color: string; bg: string }> = {
  DEAD:      { label: '已死庫存', color: '#b71c1c', bg: '#ffebee' },
  POTENTIAL: { label: '有潛力',   color: '#ef6c00', bg: '#fff3e0' },
  SLOW:      { label: '緩慢',     color: '#6a1b9a', bg: '#f3e5f5' },
  HEALTHY:   { label: '健康',     color: '#1b5e20', bg: '#e8f5e9' },
  NEW:       { label: '新品',     color: '#1565c0', bg: '#e3f2fd' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return '—';
  }
}

function formatMoney(n: number): string {
  if (!n) return 'NT$0';
  return `NT$${Math.round(n).toLocaleString()}`;
}

function formatPrice(min: number, max: number): string {
  if (!min && !max) return '—';
  if (!max || min === max) return `NT$${Math.round(min).toLocaleString()}`;
  return `NT$${Math.round(min).toLocaleString()}~${Math.round(max).toLocaleString()}`;
}

const InventoryHealthPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [tier, setTier] = useState<TierFilter>('DEAD');
  const [season, setSeason] = useState<SeasonFilter>('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  // Default to 0 (no days-since-sale filter). The tier selection (DEAD/SLOW/...)
  // is the primary classifier.
  const [daysSinceSale, setDaysSinceSale] = useState<number>(0);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortBy, setSortBy] = useState<string>('tier_priority');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [limit] = useState(200);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<InventoryResponse | null>(null);

  // Delist dialog (single-item). 下架 always targets BOTH storefront + Shopee;
  // the backend silently skips a channel the SKU isn't on.
  const [delistOpen, setDelistOpen] = useState(false);
  const [delistTarget, setDelistTarget] = useState<InventoryItem | null>(null);
  const [delistLoading, setDelistLoading] = useState(false);

  // Hover image preview — shows a larger version centered on screen when
  // user hovers any thumbnail in the table.
  const [hoveredImage, setHoveredImage] = useState<{ src: string; title: string } | null>(null);

  // Batch selection + bulk delist
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        tier,
        limit,
        offset,
        sortBy,
        sortDir,
        includeArchived: includeArchived ? 'true' : 'false',
      };
      if (daysSinceSale > 0) params.daysSinceSale = daysSinceSale;
      if (searchDebounced) params.search = searchDebounced;
      if (season) params.season = season;
      const { data: resp } = await api.get<InventoryResponse>(
        '/api/storefront/admin/inventory-health',
        { params }
      );
      setData(resp);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '載入失敗', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tier, season, daysSinceSale, searchDebounced, includeArchived, sortBy, sortDir, limit, offset, enqueueSnackbar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [tier, season, daysSinceSale, searchDebounced, includeArchived]);

  // Reset selection whenever filters/data change (keeps selection consistent with the visible page)
  useEffect(() => { setSelectedSkus(new Set()); }, [tier, season, daysSinceSale, searchDebounced, includeArchived, sortBy, sortDir, offset]);

  // Optimistically apply a delist outcome to local state so we don't need to refetch.
  // 下架 always archives the storefront product. If includeArchived=false the row
  // is removed and filtered / tier counts decremented.
  const applyDelistResult = useCallback((sku: string) => {
    setData(prev => {
      if (!prev) return prev;
      const newProducts: InventoryItem[] = [];
      let removedCount = 0;
      const removedTierCounts: Partial<Record<TierLabel, number>> = {};
      for (const p of prev.products) {
        if (p.sku !== sku) { newProducts.push(p); continue; }
        const updated: InventoryItem = {
          ...p,
          storefront: { ...p.storefront, status: 'archived' },
        };
        if (!includeArchived) {
          removedCount += 1;
          removedTierCounts[updated.tier] = (removedTierCounts[updated.tier] || 0) + 1;
          continue; // drop from visible list
        }
        newProducts.push(updated);
      }
      if (removedCount === 0) {
        return { ...prev, products: newProducts };
      }
      const newTierCounts = { ...prev.tier_counts };
      for (const [k, v] of Object.entries(removedTierCounts)) {
        const key = k as TierLabel;
        newTierCounts[key] = Math.max(0, (newTierCounts[key] || 0) - (v || 0));
      }
      return {
        ...prev,
        products: newProducts,
        filtered: Math.max(0, prev.filtered - removedCount),
        returned: Math.max(0, prev.returned - removedCount),
        tier_counts: newTierCounts,
      };
    });
  }, [includeArchived]);

  // Interpret a delist API result. Storefront result drives success/failure.
  // A Shopee "not found" is expected (SKU may not exist on Shopee) and is NOT
  // an error. Any other Shopee failure is surfaced as a warning.
  const isShopeeNotFound = (shopee: any): boolean => {
    if (!shopee || shopee.ok !== false) return false;
    const msg = String(shopee.message || '').toLowerCase();
    return msg.includes('not found');
  };

  const openDelist = (item: InventoryItem) => {
    setDelistTarget(item);
    setDelistOpen(true);
  };

  const handleDelist = async () => {
    if (!delistTarget || !delistTarget.sku) return;
    const sku = delistTarget.sku;
    setDelistLoading(true);
    try {
      const { data: resp } = await api.post('/api/storefront/admin/delist-product', {
        sku,
        targets: { shopee: true, storefront: true },
      });
      const r = resp.result || {};
      const storefrontOk = !!r.storefront?.ok;
      if (storefrontOk) {
        // Shopee "not found" is expected & silent; other Shopee failures warn.
        if (r.shopee && r.shopee.ok === false && !isShopeeNotFound(r.shopee)) {
          enqueueSnackbar(`官網已下架，但 Shopee 失敗：${r.shopee.message}`, { variant: 'warning' });
        } else {
          enqueueSnackbar('已下架', { variant: 'success' });
        }
        applyDelistResult(sku);
        // Also drop from selection if it was selected
        setSelectedSkus(prev => {
          if (!prev.has(sku)) return prev;
          const next = new Set(prev);
          next.delete(sku);
          return next;
        });
        setDelistOpen(false);
        setDelistTarget(null);
      } else {
        enqueueSnackbar(`官網下架失敗：${r.storefront?.message || '未知錯誤'}`, { variant: 'error' });
      }
      // No fetchData() — local state is already in sync.
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || '下架失敗', { variant: 'error' });
    } finally {
      setDelistLoading(false);
    }
  };

  // Batch selection helpers — only SKU'd rows can be selected (delist keys by SKU)
  const toggleSelect = (sku: string) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };

  const visibleSkus = (data?.products || [])
    .map(p => p.sku)
    .filter((s): s is string => !!s);
  const selectedVisibleCount = visibleSkus.filter(s => selectedSkus.has(s)).length;
  const allVisibleSelected = visibleSkus.length > 0 && selectedVisibleCount === visibleSkus.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = () => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleSkus.forEach(s => next.delete(s));
      } else {
        visibleSkus.forEach(s => next.add(s));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedSkus(new Set());

  const openBulkDelist = () => {
    if (selectedSkus.size === 0) return;
    setBulkOpen(true);
  };

  const handleBulkDelist = async () => {
    const skus = Array.from(selectedSkus);
    if (skus.length === 0) return;
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: skus.length });
    let ok = 0;
    let fail = 0;
    const concurrency = 4;
    const processOne = async (sku: string) => {
      try {
        const { data: resp } = await api.post('/api/storefront/admin/delist-product', {
          sku,
          targets: { shopee: true, storefront: true },
        });
        const r = resp.result || {};
        if (r.storefront?.ok) { ok += 1; applyDelistResult(sku); } else { fail += 1; }
      } catch {
        fail += 1;
      } finally {
        setBulkProgress(p => p ? { done: p.done + 1, total: p.total } : null);
      }
    };
    // Run in small parallel batches so we don't hammer the API
    for (let i = 0; i < skus.length; i += concurrency) {
      const batch = skus.slice(i, i + concurrency);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(batch.map(processOne));
    }
    setBulkLoading(false);
    setBulkProgress(null);
    setBulkOpen(false);
    setSelectedSkus(new Set());
    enqueueSnackbar(`批次下架完成：成功 ${ok} / 失敗 ${fail}`, {
      variant: fail > 0 ? 'warning' : 'success',
    });
  };

  const tierCounts = data?.tier_counts || { DEAD: 0, POTENTIAL: 0, SLOW: 0, HEALTHY: 0, NEW: 0 };

  const summaryCards = useMemo(() => ([
    { key: 'DEAD' as TierLabel, label: '已死庫存', sub: '0 銷量 180天' },
    { key: 'POTENTIAL' as TierLabel, label: '有潛力', sub: '有流量沒轉換' },
    { key: 'SLOW' as TierLabel, label: '緩慢', sub: '1-4 件 180天' },
    { key: 'HEALTHY' as TierLabel, label: '健康', sub: '≥5 件 180天' },
    { key: 'NEW' as TierLabel, label: '新品', sub: '上架 <30天' },
  ]), []);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir(key === 'tier_priority' ? 'asc' : 'desc');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Centered hover preview — non-interactive, so it won't steal the hover */}
      {hoveredImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <Box
            sx={{
              maxWidth: '80vw',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src={hoveredImage.src}
              alt={hoveredImage.title}
              sx={{
                maxWidth: '80vw',
                maxHeight: '78vh',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                backgroundColor: '#fff',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                maxWidth: '80vw',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {hoveredImage.title}
            </Typography>
          </Box>
        </Box>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: PRIMARY, fontWeight: 600 }}>
            庫存健康度 / 死庫存管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            檢視官網全品項近 180 天表現，識別死庫存與有潛力商品，一鍵下架。下架會同時處理官網與蝦皮。
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            重新載入
          </Button>
        </Stack>
      </Stack>

      {/* Tier summary cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {summaryCards.map(c => {
          const meta = TIER_META[c.key];
          const active = tier === c.key;
          return (
            <Card
              key={c.key}
              onClick={() => setTier(c.key)}
              sx={{
                p: 2, minWidth: 160, cursor: 'pointer',
                bgcolor: active ? meta.bg : 'background.paper',
                border: `2px solid ${active ? meta.color : 'transparent'}`,
                transition: 'all 0.2s',
                '&:hover': { borderColor: meta.color },
              }}
            >
              <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
                {c.label}
              </Typography>
              <Typography variant="h5" sx={{ color: meta.color, fontWeight: 700, my: 0.5 }}>
                {tierCounts[c.key] ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.sub}
              </Typography>
            </Card>
          );
        })}
        <Card
          onClick={() => setTier('all')}
          sx={{
            p: 2, minWidth: 120, cursor: 'pointer',
            bgcolor: tier === 'all' ? '#eceff1' : 'background.paper',
            border: `2px solid ${tier === 'all' ? '#546e7a' : 'transparent'}`,
          }}
        >
          <Typography variant="caption" sx={{ color: '#546e7a', fontWeight: 600 }}>
            全部
          </Typography>
          <Typography variant="h5" sx={{ color: '#37474f', fontWeight: 700, my: 0.5 }}>
            {data?.eligible_count ?? data?.filtered ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            符合條件
          </Typography>
        </Card>
      </Stack>

      {/* Filters row */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>未售天數 ≥</InputLabel>
            <Select
              label="未售天數 ≥"
              value={daysSinceSale}
              onChange={(e) => setDaysSinceSale(Number(e.target.value))}
            >
              <MenuItem value={0}>不限</MenuItem>
              <MenuItem value={30}>30 天</MenuItem>
              <MenuItem value={60}>60 天</MenuItem>
              <MenuItem value={90}>90 天</MenuItem>
              <MenuItem value={180}>180 天</MenuItem>
              <MenuItem value={365}>365 天</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>季節</InputLabel>
            <Select
              label="季節"
              value={season}
              onChange={(e) => setSeason(e.target.value as SeasonFilter)}
            >
              <MenuItem value="">全部</MenuItem>
              <MenuItem value="春">春</MenuItem>
              <MenuItem value="夏">夏</MenuItem>
              <MenuItem value="秋">秋</MenuItem>
              <MenuItem value="冬">冬</MenuItem>
              <MenuItem value="四季">四季</MenuItem>
              <MenuItem value="none">無季節資料</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="搜尋 SKU、商品名、類型"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ minWidth: 280 }}
          />

          <FormControlLabel
            control={<Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
            label="包含已下架"
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !data || data.products.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">沒有符合條件的商品</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
              <Typography variant="body2" color="text.secondary">
                共 <strong>{data.filtered}</strong> 筆 / 全站 {data.total_sku_count} 件商品 — 顯示 {data.offset + 1}-{data.offset + data.returned}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >上一頁</Button>
                <Button
                  size="small"
                  disabled={offset + data.returned >= data.filtered}
                  onClick={() => setOffset(offset + limit)}
                >下一頁</Button>
              </Stack>
            </Box>

            {/* Batch action bar — visible only when something is selected */}
            {selectedSkus.size > 0 && (
              <Box sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#fff3e0',
                borderBottom: '1px solid #ffcc80',
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                  已選 {selectedSkus.size} 筆 SKU
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={clearSelection} disabled={bulkLoading}>
                    清除選擇
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={openBulkDelist}
                    disabled={bulkLoading}
                  >
                    批次下架 ({selectedSkus.size})
                  </Button>
                </Stack>
              </Box>
            )}

            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someVisibleSelected}
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 60 }}>圖片</TableCell>
                    <TableCell sx={{ width: 80 }}>SKU</TableCell>
                    <TableCell>商品名稱</TableCell>
                    <TableCell align="right" sx={{ width: 70 }}>庫存</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>價格</TableCell>
                    <TableCell sx={{ width: 90 }}>狀態</TableCell>
                    <TableCell sx={{ width: 90 }}>季節</TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <TableSortLabel
                        active={sortBy === 'tier_priority'}
                        direction={sortBy === 'tier_priority' ? sortDir : 'asc'}
                        onClick={() => handleSort('tier_priority')}
                      >
                        分類
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      <TableSortLabel
                        active={sortBy === 'days_since_listing'}
                        direction={sortBy === 'days_since_listing' ? sortDir : 'desc'}
                        onClick={() => handleSort('days_since_listing')}
                      >
                        上架天數
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      <TableSortLabel
                        active={sortBy === 'days_since_sale'}
                        direction={sortBy === 'days_since_sale' ? sortDir : 'desc'}
                        onClick={() => handleSort('days_since_sale')}
                      >
                        未售天數
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      <TableSortLabel
                        active={sortBy === 'units_180d'}
                        direction={sortBy === 'units_180d' ? sortDir : 'desc'}
                        onClick={() => handleSort('units_180d')}
                      >
                        180天銷量
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      <TableSortLabel
                        active={sortBy === 'views_180d'}
                        direction={sortBy === 'views_180d' ? sortDir : 'desc'}
                        onClick={() => handleSort('views_180d')}
                      >
                        180天瀏覽
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      <Tooltip title="加入購物車率 = ATC / 瀏覽。2%+ 算有興趣，<1% 算沒興趣。資料不足時顯示 —">
                        <TableSortLabel
                          active={sortBy === 'atc_rate_180d'}
                          direction={sortBy === 'atc_rate_180d' ? sortDir : 'desc'}
                          onClick={() => handleSort('atc_rate_180d')}
                        >
                          ATC率
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      <Tooltip title="轉換率 = 銷量 / 瀏覽。0.5%+ 算健康，1%+ 算強。資料不足時顯示 —">
                        <TableSortLabel
                          active={sortBy === 'cvr_180d'}
                          direction={sortBy === 'cvr_180d' ? sortDir : 'desc'}
                          onClick={() => handleSort('cvr_180d')}
                        >
                          轉換率
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <TableSortLabel
                        active={sortBy === 'revenue_180d'}
                        direction={sortBy === 'revenue_180d' ? sortDir : 'desc'}
                        onClick={() => handleSort('revenue_180d')}
                      >
                        180天營收
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 120 }} align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.products.map((p) => {
                    const meta = TIER_META[p.tier];
                    const hasSku = !!p.sku;
                    const isSelected = hasSku && selectedSkus.has(p.sku as string);
                    return (
                      <TableRow
                        key={p.storefront?.product_id ?? p.sku ?? p.title}
                        hover
                        selected={isSelected}
                      >
                        <TableCell padding="checkbox">
                          <Tooltip title={hasSku ? '' : '無 SKU，無法下架'}>
                            <span>
                              <Checkbox
                                checked={isSelected}
                                disabled={!hasSku}
                                onChange={() => hasSku && toggleSelect(p.sku as string)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {p.image_url ? (
                            <Box
                              component="img"
                              src={p.image_url}
                              alt={p.title}
                              onMouseEnter={() => setHoveredImage({ src: p.image_url, title: p.title })}
                              onMouseLeave={() => setHoveredImage(null)}
                              sx={{
                                width: 48,
                                height: 48,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'zoom-in',
                                transition: 'transform 0.15s',
                                '&:hover': { transform: 'scale(1.1)' },
                              }}
                            />
                          ) : (
                            <Box sx={{ width: 48, height: 48, bgcolor: '#eee', borderRadius: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {p.sku || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Tooltip title={p.reasons.join(' · ')}>
                            {p.storefront?.handle ? (
                              <Link
                                href={`${data.storefront_base_url}/products/${p.storefront.handle}`}
                                target="_blank"
                                rel="noopener"
                                underline="hover"
                                variant="body2"
                                sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}
                              >
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.title}
                                </Box>
                                <OpenInNewIcon sx={{ fontSize: 12, ml: 0.3, flexShrink: 0 }} />
                              </Link>
                            ) : (
                              <Typography variant="body2" noWrap>
                                {p.title}
                              </Typography>
                            )}
                          </Tooltip>
                          {p.product_type && (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip size="small" variant="outlined" label={p.product_type} sx={{ height: 20, fontSize: 11 }} />
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">{p.storefront?.stock_total ?? 0}</TableCell>
                        <TableCell align="right">
                          {formatPrice(p.storefront?.price_min ?? 0, p.storefront?.price_max ?? 0)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={p.storefront?.status === 'active' ? '上架中' : (p.storefront?.status || '—')}
                            color={p.storefront?.status === 'active' ? 'success' : 'default'}
                            variant={p.storefront?.status === 'active' ? 'filled' : 'outlined'}
                            sx={{ height: 22 }}
                          />
                        </TableCell>
                        <TableCell>
                          {p.seasons && p.seasons.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {p.seasons.join('/')}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={meta.label}
                            sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {p.days_since_listing ?? '—'}
                          {p.listed_at && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDate(p.listed_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {p.days_since_sale !== null ? (
                            <Typography
                              sx={{ color: p.days_since_sale >= 60 ? '#b71c1c' : 'inherit', fontWeight: p.days_since_sale >= 60 ? 600 : 400 }}
                            >
                              {p.days_since_sale}
                            </Typography>
                          ) : '—'}
                          {p.last_sale_date && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDate(p.last_sale_date)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{p.units_180d}</TableCell>
                        <TableCell align="right">{p.views_180d.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {p.atc_rate_180d !== null ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: p.atc_rate_180d >= 0.04 ? '#2e7d32'
                                     : p.atc_rate_180d >= 0.02 ? '#ef6c00'
                                     : '#c62828',
                                fontWeight: p.atc_rate_180d >= 0.02 ? 600 : 400,
                              }}
                            >
                              {(p.atc_rate_180d * 100).toFixed(1)}%
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                          {p.atc_180d > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {p.atc_180d.toLocaleString()}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {p.cvr_180d !== null ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: p.cvr_180d >= 0.01 ? '#2e7d32'
                                     : p.cvr_180d >= 0.005 ? '#ef6c00'
                                     : '#c62828',
                                fontWeight: p.cvr_180d >= 0.005 ? 600 : 400,
                              }}
                            >
                              {(p.cvr_180d * 100).toFixed(2)}%
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                          {p.atc_to_buy_180d !== null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              購買率 {(p.atc_to_buy_180d * 100).toFixed(0)}%
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatMoney(p.revenue_180d)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={hasSku ? '下架此商品' : '無 SKU，無法下架'}>
                            <span>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={!hasSku}
                                onClick={() => openDelist(p)}
                              >
                                下架
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Delist dialog */}
      <Dialog open={delistOpen} onClose={() => setDelistOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ef6c00' }} /> 下架商品
        </DialogTitle>
        <DialogContent>
          {delistTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {delistTarget.image_url && (
                  <Box component="img" src={delistTarget.image_url} sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }} />
                )}
                <Box>
                  <Typography variant="body1" fontWeight={600}>{delistTarget.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {delistTarget.sku} · 180天銷量: {delistTarget.units_180d} · 瀏覽: {delistTarget.views_180d}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                將同時下架官網與蝦皮（若蝦皮無此商品則自動略過）。
              </Typography>

              <Typography variant="caption" sx={{ color: '#b71c1c' }}>
                注意：官網端會將 status 改為 archived；蝦皮端會呼叫 updateProductStatus=UNLIST。可重新上架。
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelistOpen(false)} disabled={delistLoading}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelist}
            disabled={delistLoading || !delistTarget?.sku}
            startIcon={delistLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          >
            確認下架
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delist dialog */}
      <Dialog
        open={bulkOpen}
        onClose={() => !bulkLoading && setBulkOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ef6c00' }} /> 批次下架 ({selectedSkus.size} 筆)
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              已選 <strong>{selectedSkus.size}</strong> 個 SKU。
            </Typography>

            <Typography variant="body2" color="text.secondary">
              將同時下架官網與蝦皮（若蝦皮無此商品則自動略過）。
            </Typography>

            {bulkProgress && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  進度 {bulkProgress.done} / {bulkProgress.total}
                </Typography>
                <Box sx={{
                  mt: 0.5,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#eee',
                  overflow: 'hidden',
                }}>
                  <Box sx={{
                    width: `${Math.round((bulkProgress.done / Math.max(bulkProgress.total, 1)) * 100)}%`,
                    height: '100%',
                    bgcolor: PRIMARY,
                    transition: 'width 0.3s',
                  }} />
                </Box>
              </Box>
            )}

            <Typography variant="caption" sx={{ color: '#b71c1c' }}>
              注意：批次動作為並行呼叫（每次 4 筆），之後可從官網 / 蝦皮後台重新上架。
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkLoading}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelist}
            disabled={bulkLoading || selectedSkus.size === 0}
            startIcon={bulkLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {bulkLoading ? '下架中…' : `確認下架 ${selectedSkus.size} 筆`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryHealthPage;
